import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"

import { Certification } from "@/modules/companies/client/components/Certification/Certification"
import { Faq } from "@/modules/companies/client/components/Faq/Faq"
import { RECRUITMENT_PATTERN_ID } from "@/modules/companies/client/components/RecruitmentPatternInformation/RecruitmentPatternInformation"
import { COMPANY_PAGE_STATISTICS } from "@/modules/companies/client/constants/flags"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { COMPANY_BASENAME, COMPANY_JOB_OFFERS } from "@/modules/companies/client/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./WorkThere.module.css"

const RecruitmentPatternBannerDynamic = dynamic(() =>
  import("@/modules/companies/client/components/RecruitmentPatternBanner/RecruitmentPatternBanner").then(
    mod => mod.RecruitmentPatternBanner
  )
)

const InsightsDynamic = dynamic(
  () => import("@/modules/companies/client/components/Insights/Insights").then(mod => mod.Insights),
  {
    ssr: false,
  }
)

export const WorkThere: FunctionComponent = () => {
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCompany } = useCompanyFetch()
  const { t } = useTranslation(["companies_client"])
  const isStatisticsModuleActive = useFlag(COMPANY_PAGE_STATISTICS)
  const { companySlug } = useParams()

  const name = dataCompany?.page.name || ""

  return (
    <Layout hasBottomPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE}>
      <Layout.Column>
        <Heading
          className={styles.title}
          data-testid="company_information_what_title"
          as="h2"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.how.title", { name })}
        </Heading>
        <div className={styles.container}>
          <div className={styles.content}>
            <Faq className={styles.faq} />
          </div>
          <div className={styles.content}>
            {dataCurrentUser?.isLogged && <InsightsDynamic className={styles.insights} />}
            <Certification className={styles.certifications} />
          </div>
          {isStatisticsModuleActive && (
            <div className={styles.content}>
              <RecruitmentPatternBannerDynamic
                className={styles.recruitmentBanner}
                href={`/${locale}/${COMPANY_BASENAME}/${companySlug}/${COMPANY_JOB_OFFERS}#${RECRUITMENT_PATTERN_ID}`}
                linkComponent={Link}
              />
            </div>
          )}
        </div>
      </Layout.Column>
    </Layout>
  )
}
